import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Download, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LeadMagnet() {
  const [form, setForm] = useState({ full_name: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email) {
      toast.error("Please add your name and email.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/lead-magnet`, form);
      setDone(true);
      toast.success("Check your inbox — your VIP Perks are on the way.");
      // Immediate download
      window.open(`${API}/lead-magnet/perks.pdf`, "_blank");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="perks-pdf" className="relative bg-[#1A1A1A] text-[#F9F6F0] py-24 md:py-32 z-[2]" data-testid="lead-magnet-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 md:gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9 }}
            className="col-span-12 md:col-span-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">Complimentary · PDF</span>
            </div>
            <h2 className="font-serif-editorial text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              The full<br /><span className="italic text-[#E6C5C0]">perks list.</span>
            </h2>
            <p className="mt-8 text-[#F9F6F0]/75 leading-relaxed max-w-md">
              A one-page dossier of all twenty-two VIP amenities — designed to print, save, and share. Sent to your inbox, no membership required.
            </p>

            <div className="mt-10 hidden md:flex items-center gap-8 pt-6 border-t border-[#F9F6F0]/15">
              <div>
                <p className="font-serif-editorial italic text-3xl text-[#C5A059]">22</p>
                <p className="eyebrow text-[#F9F6F0]/60 mt-1">Perks</p>
              </div>
              <div>
                <p className="font-serif-editorial italic text-3xl text-[#C5A059]">$0</p>
                <p className="eyebrow text-[#F9F6F0]/60 mt-1">Membership</p>
              </div>
              <div>
                <p className="font-serif-editorial italic text-3xl text-[#C5A059]">8</p>
                <p className="eyebrow text-[#F9F6F0]/60 mt-1">Resorts</p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 md:col-span-5 md:col-start-8"
          >
            {done ? (
              <div className="bg-[#F9F6F0] text-[#1A1A1A] p-10" data-testid="lead-magnet-success">
                <p className="eyebrow text-[#C5A059] mb-4">On its way</p>
                <h3 className="font-serif-editorial text-3xl italic mb-4">Enjoy the list, {form.full_name.split(" ")[0]}.</h3>
                <p className="text-[#5C5C5C] leading-relaxed mb-8">Your VIP Perks PDF has been sent to <strong>{form.email}</strong>. If a download didn&rsquo;t open automatically, tap below.</p>
                <a href={`${API}/lead-magnet/perks.pdf`} target="_blank" rel="noopener noreferrer"
                   className="ee-btn-primary" data-testid="btn-manual-download">
                  Download PDF <Download size={14} strokeWidth={1.5} />
                </a>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-[#F9F6F0] text-[#1A1A1A] p-8 md:p-10" data-testid="lead-magnet-form">
                <p className="eyebrow text-[#C5A059] mb-6">Send me the perks</p>
                <div className="space-y-6">
                  <div>
                    <label className="eyebrow text-[#5C5C5C]">Full Name</label>
                    <input
                      className="ee-input" required
                      value={form.full_name}
                      onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
                      data-testid="lm-input-name"
                    />
                  </div>
                  <div>
                    <label className="eyebrow text-[#5C5C5C]">Email</label>
                    <input
                      className="ee-input" required type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      data-testid="lm-input-email"
                    />
                  </div>
                </div>
                <button type="submit" disabled={submitting} className="ee-btn-primary mt-8" data-testid="lm-submit">
                  {submitting ? "Sending..." : "Get the PDF"}
                  {!submitting && <ArrowUpRight size={14} strokeWidth={1.5} />}
                </button>
                <p className="text-xs text-[#5C5C5C] mt-6">One email. No spam. Unsubscribe any time.</p>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
